// M5 – Đơn nháp (6.5): xem nháp mọi thu ngân, lọc, đổi tên, xóa nhiều, mở khóa, mở tiếp trên màn bán hàng
import { useMemo, useState } from 'react';
import { Input, Modal, Select, Switch, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { Eraser, FolderOpen, Lock, Pen, Trash2, Unlock } from 'lucide-react';
import { branchName, type Draft } from './data';
import usePosStore from './store';
import { branchOptions, draftDaysLeft, draftLockInfo, fmtDateTime, formatDaysLeft, money, usePosAuth } from './utils';
import PosShell from './components/PosShell';

export default function PosDrafts() {
  const navigate = useNavigate();
  const drafts = usePosStore(s => s.drafts);
  const customers = usePosStore(s => s.customers);
  const ttl = usePosStore(s => s.config.draftTtlDays);
  const workingBranch = usePosStore(s => s.workingBranch);
  const renameDraft = usePosStore(s => s.renameDraft);
  const deleteDrafts = usePosStore(s => s.deleteDrafts);
  const unlockDraft = usePosStore(s => s.unlockDraft);
  const purgeExpiredDrafts = usePosStore(s => s.purgeExpiredDrafts);
  const { can, role, userName } = usePosAuth();
  const [modal, ctx] = Modal.useModal();

  const [branch, setBranch] = useState<string | undefined>(role === 'cashier' ? workingBranch : undefined);
  const [creator, setCreator] = useState<string | undefined>();
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  const creators = useMemo(() => Array.from(new Set(drafts.map(d => d.createdBy))).sort(), [drafts]);
  const data = useMemo(() => drafts
    .filter(d => (!branch || d.branchId === branch) && (!creator || d.createdBy === creator) && (!mine || d.createdBy === userName) &&
      (!q || d.name.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)), [drafts, branch, creator, mine, q, userName]);

  const canManage = (d: Draft) => d.createdBy === userName || can('delete');
  const expiredCount = drafts.filter(d => draftDaysLeft(d.updatedAt, ttl) <= 0).length;

  const open = (d: Draft) => {
    const lock = draftLockInfo(d, userName);
    if (lock.locked) return message.error(`Đơn nháp đang được ${lock.by} xử lý`);
    navigate(`/pos?draft=${d.id}`);
  };

  const saveName = () => {
    if (!editing) return;
    const v = editing.name.trim();
    if (!v || v.length > 60) return message.error('Tên 1–60 ký tự');
    renameDraft(editing.id, v);
    setEditing(null);
    message.success('Đã đổi tên');
  };

  const removable = selected.filter(id => { const d = drafts.find(x => x.id === id); return d && canManage(d); });

  const columns: ColumnsType<Draft> = [
    {
      key: 'name', title: 'Tên gợi nhớ', width: 240,
      render: (_, d) => editing?.id === d.id ? (
        <Input autoFocus size="small" maxLength={60} value={editing.name} onChange={e => setEditing({ id: d.id, name: e.target.value })} onPressEnter={saveName} onBlur={saveName} />
      ) : (
        <button disabled={!canManage(d)} onClick={() => setEditing({ id: d.id, name: d.name })} className="flex items-center gap-1.5 text-left font-semibold text-slate-800 hover:text-[#2c6b7f]">
          {d.name}{canManage(d) && <Pen className="h-3 w-3 text-slate-400" />}
        </button>
      ),
    },
    { key: 'customer', title: 'Khách', width: 170, render: (_, d) => customers.find(c => c.id === d.customerId)?.name || d.appointment?.customer?.name || 'Khách lẻ' },
    { key: 'lines', title: 'Số dòng', width: 80, align: 'right', render: (_, d) => d.lines.length },
    { key: 'total', title: 'Tạm tính', width: 130, align: 'right', render: (_, d) => <b>{money(d.total)}</b> },
    { key: 'createdBy', title: 'Người tạo', width: 110, dataIndex: 'createdBy' },
    { key: 'branch', title: 'Chi nhánh', width: 140, render: (_, d) => branchName(d.branchId) },
    { key: 'updatedAt', title: 'Cập nhật lúc', width: 150, render: (_, d) => fmtDateTime(d.updatedAt) },
    {
      key: 'expires', title: 'Hết hạn', width: 150,
      render: (_, d) => { const days = draftDaysLeft(d.updatedAt, ttl); return <span className={days < 1 ? 'font-semibold text-red-600' : 'text-slate-500'}>{formatDaysLeft(days)}</span>; },
    },
    {
      key: 'lock', title: 'Khóa', width: 160,
      render: (_, d) => {
        const l = draftLockInfo(d, userName);
        if (l.locked) return <span className="flex items-center gap-1 text-amber-700"><Lock className="h-3.5 w-3.5" />{l.by} ({l.minutes} phút)</span>;
        if ('stale' in l && l.stale) return <span className="text-xs text-slate-400">Khóa của {l.by} đã hết hạn</span>;
        return <span className="text-slate-400">—</span>;
      },
    },
    {
      key: 'actions', title: '', width: 210, fixed: 'right',
      render: (_, d) => {
        const l = draftLockInfo(d, userName);
        return (
          <div className="flex items-center justify-end gap-1">
            {l.locked && can('delete') && (
              <button title="Mở khóa cưỡng bức" onClick={() => modal.confirm({ title: 'Mở khóa cưỡng bức?', content: `${l.by} đang mở đơn nháp này.`, okText: 'Mở khóa', cancelText: 'Hủy', onOk: () => { unlockDraft(d.id); message.success('Đã mở khóa'); } })}
                className="flex h-8 w-8 items-center justify-center rounded text-amber-600 hover:bg-amber-50"><Unlock className="h-4 w-4" /></button>
            )}
            {canManage(d) && (
              <button title="Xóa" onClick={() => modal.confirm({ title: 'Xóa đơn nháp?', content: d.name, okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy', onOk: () => { deleteDrafts([d.id]); message.success('Đã xóa'); } })}
                className="flex h-8 w-8 items-center justify-center rounded text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            )}
            <button disabled={l.locked} onClick={() => open(d)} className="flex h-8 items-center gap-1 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white hover:bg-[#2c6b7f] disabled:bg-slate-200 disabled:text-slate-400">
              <FolderOpen className="h-4 w-4" /> Mở tiếp
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <PosShell
      title="Đơn nháp"
      subtitle={`${data.length} đơn · tự hết hạn sau ${ttl} ngày không cập nhật · khóa tự nhả sau 15 phút`}
      actions={expiredCount > 0 && can('delete') ? (
        <button onClick={() => { const n = purgeExpiredDrafts(); message.success(`Đã dọn ${n} nháp hết hạn`); }} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          <Eraser className="h-4 w-4" /> Dọn {expiredCount} nháp hết hạn
        </button>
      ) : null}
    >
      {ctx}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select allowClear placeholder="Chi nhánh" className="w-44" value={branch} onChange={setBranch} options={branchOptions} disabled={role === 'cashier'} />
        <Select allowClear placeholder="Người tạo" className="w-40" value={creator} onChange={setCreator} options={creators.map(c => ({ value: c, label: c }))} />
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch size="small" checked={mine} onChange={setMine} /> Của tôi</label>
        <Input.Search allowClear placeholder="Tìm theo tên nháp…" value={q} onChange={e => setQ(e.target.value)} className="w-60" />
        {selected.length > 0 && (
          <button disabled={!removable.length}
            onClick={() => modal.confirm({ title: `Xóa ${removable.length} đơn nháp?`, content: removable.length < selected.length ? `${selected.length - removable.length} nháp của người khác không đủ quyền xóa sẽ được bỏ qua.` : undefined, okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy', onOk: () => { deleteDrafts(removable); setSelected([]); message.success('Đã xóa'); } })}
            className="flex h-8 items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 text-sm font-semibold text-red-600 disabled:opacity-50">
            <Trash2 className="h-4 w-4" /> Xóa {selected.length} đã chọn
          </button>
        )}
      </div>
      <Table<Draft>
        rowKey="id" size="middle" className="custom-ant-table" columns={columns} dataSource={data} scroll={{ x: 'max-content' }}
        rowSelection={{ selectedRowKeys: selected, onChange: k => setSelected(k as string[]) }}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: 'Không có đơn nháp' }}
      />
    </PosShell>
  );
}
